// src/pages/AmazonCalculatorPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Calculator,
  Save,
  Trash2,
  RotateCcw,
  Bookmark,
  X,
  AlertTriangle,
  Loader2,
  Check,
  Target,
} from 'lucide-react';
import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  getDocs,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
  calculateFBA,
  calculateFBM,
  calculateTargetPrice,
  normalizeInputs,
  DEFAULT_INPUTS,
} from '../data/amazonCalculator';
import { REFERRAL_FEES, FBA_SIZE_TIERS } from '../data/amazonFees';
import AmazonFeeBreakdown from '../components/AmazonFeeBreakdown';

const LAST_INPUTS_KEY = 'amazonCalculator_lastInputs';

function Field({ label, children, hint, warning }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
        {label}
      </label>
      {children}
      {warning && (
        <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-400">
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
          <span>{warning}</span>
        </div>
      )}
      {hint && !warning && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

const inputClass =
  'w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition';
const invalidInputClass =
  'w-full bg-slate-900/60 border border-red-500 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 transition';

function NumberInput({ value, onChange, placeholder, step = '0.01', min = 0, prefix }) {
  const isInvalid = value !== '' && parseFloat(value) < 0;
  return (
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
          {prefix}
        </span>
      )}
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        step={step}
        min={min}
        className={`${isInvalid ? invalidInputClass : inputClass} ${prefix ? 'pl-7' : ''}`}
      />
    </div>
  );
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-slate-800/50 backdrop-blur-md rounded-xl border border-slate-700/50 p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-200 uppercase tracking-wider mb-4">
        {Icon && <Icon size={16} className="text-indigo-400" />}
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export default function AmazonCalculatorPage({ user }) {
  const [mode, setMode] = useState('fba'); // 'fba' | 'fbm' | 'compare'
  const [inputs, setInputs] = useState(() => {
    try {
      const saved = localStorage.getItem(LAST_INPUTS_KEY);
      if (saved) return { ...DEFAULT_INPUTS, ...JSON.parse(saved) };
    } catch (e) {
      // ignore corrupt localStorage
    }
    return DEFAULT_INPUTS;
  });
  const [savedDrawerOpen, setSavedDrawerOpen] = useState(false);
  const [savedCalcs, setSavedCalcs] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savedError, setSavedError] = useState('');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }

  // Persist to localStorage on every input change
  useEffect(() => {
    try {
      localStorage.setItem(LAST_INPUTS_KEY, JSON.stringify(inputs));
    } catch (e) {
      // ignore quota errors
    }
  }, [inputs]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const updateInput = (key, value) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const updateDimension = (key, value) => {
    setInputs((prev) => ({
      ...prev,
      dimensions: { ...prev.dimensions, [key]: value },
    }));
  };

  const resetForm = () => {
    setInputs(DEFAULT_INPUTS);
    setToast({ type: 'success', message: 'Form reset' });
  };

  // Normalized inputs + calculations
  const normalized = useMemo(() => normalizeInputs(inputs), [inputs]);
  const hasInputs = normalized.productCost > 0 && normalized.salePrice > 0;

  const fbaResult = useMemo(() => calculateFBA(normalized), [normalized]);
  const fbmResult = useMemo(() => calculateFBM(normalized), [normalized]);

  const targetProfitNum = normalized.targetProfit;
  const requiredPriceFBA = useMemo(
    () => (targetProfitNum > 0 ? calculateTargetPrice(normalized, targetProfitNum, 'fba') : null),
    [normalized, targetProfitNum],
  );
  const requiredPriceFBM = useMemo(
    () => (targetProfitNum > 0 ? calculateTargetPrice(normalized, targetProfitNum, 'fbm') : null),
    [normalized, targetProfitNum],
  );
  // Target profit only needs productCost + fee inputs — not salePrice
  const hasTargetInputs = normalized.productCost > 0 && targetProfitNum > 0;

  // Weight tier warnings
  const selectedTier = FBA_SIZE_TIERS.find((t) => t.id === inputs.sizeTierId);
  const weightWarning =
    normalized.weightOz > 0 &&
    selectedTier &&
    normalized.weightOz > selectedTier.maxWeightOz
      ? `Weight exceeds ${selectedTier.name.split(' (')[0]} max (${
          selectedTier.maxWeightOz
        } oz) — consider a larger tier`
      : null;

  const dimsOk = selectedTier?.maxDims
    ? (() => {
        const L = normalized.dimensions.length;
        const W = normalized.dimensions.width;
        const H = normalized.dimensions.height;
        if (L + W + H === 0) return true;
        const sorted = [L, W, H].sort((a, b) => b - a);
        return (
          sorted[0] <= selectedTier.maxDims.longest &&
          sorted[1] <= selectedTier.maxDims.median &&
          sorted[2] <= selectedTier.maxDims.shortest
        );
      })()
    : true;
  const dimsWarning = !dimsOk ? `Dimensions exceed ${selectedTier.name.split(' (')[0]} limits` : null;

  // Saved calculations
  const loadSaved = useCallback(async () => {
    if (!user) return;
    setLoadingSaved(true);
    setSavedError('');
    try {
      const colRef = collection(db, 'users', user.uid, 'amazonCalculations');
      const q = query(colRef, orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setSavedCalcs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Failed to load saved calculations:', err);
      setSavedError("Couldn't load saved calculations");
    } finally {
      setLoadingSaved(false);
    }
  }, [user]);

  useEffect(() => {
    if (savedDrawerOpen) loadSaved();
  }, [savedDrawerOpen, loadSaved]);

  const handleSave = async () => {
    if (!user) return;
    const label = saveLabel.trim() || `Calculation ${new Date().toLocaleDateString()}`;
    setSaving(true);
    try {
      const colRef = collection(db, 'users', user.uid, 'amazonCalculations');
      await addDoc(colRef, {
        label,
        mode,
        inputs,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setSaveModalOpen(false);
      setSaveLabel('');
      setToast({ type: 'success', message: `Saved "${label}"` });
      if (savedDrawerOpen) loadSaved();
    } catch (err) {
      console.error('Failed to save calculation:', err);
      setToast({ type: 'error', message: "Couldn't save — try again" });
    } finally {
      setSaving(false);
    }
  };

  const handleLoadCalc = (calc) => {
    setInputs({ ...DEFAULT_INPUTS, ...calc.inputs });
    setMode(calc.mode || 'fba');
    setSavedDrawerOpen(false);
    setToast({ type: 'success', message: `Loaded "${calc.label}"` });
  };

  const handleDeleteCalc = async (calcId) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'amazonCalculations', calcId));
      setSavedCalcs((prev) => prev.filter((c) => c.id !== calcId));
    } catch (err) {
      console.error('Failed to delete calculation:', err);
      setToast({ type: 'error', message: "Couldn't delete — try again" });
    }
  };

  return (
    <div className="flex-grow bg-gradient-to-br from-gray-900 via-slate-900 to-black min-h-screen text-slate-200 p-4 md:p-8 animate-fade-in">
      <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent flex items-center gap-3">
              <Calculator className="text-indigo-400" size={36} />
              Amazon FBA / FBM Calculator
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Calculate profit, margin, and ROI on your Amazon listings
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSavedDrawerOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-sm font-medium transition-all duration-200"
            >
              <Bookmark size={16} />
              Saved
            </button>
            <button
              onClick={resetForm}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-sm font-medium transition-all duration-200"
            >
              <RotateCcw size={16} />
              Reset
            </button>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="bg-slate-800/50 backdrop-blur-md rounded-xl border border-slate-700/50 p-1.5 inline-flex">
          {[
            { key: 'fba', label: 'FBA' },
            { key: 'fbm', label: 'FBM' },
            { key: 'compare', label: 'Compare' },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all ${
                mode === m.key
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Main content */}
        <div
          className={`grid gap-6 ${
            mode === 'compare'
              ? 'grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]'
              : 'grid-cols-1 lg:grid-cols-2'
          }`}
        >
          {/* LEFT: Inputs */}
          <div className="space-y-5">
            <SectionCard title="Product" icon={Calculator}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Cost ($)">
                  <NumberInput
                    value={inputs.productCost}
                    onChange={(v) => updateInput('productCost', v)}
                    placeholder="4.50"
                    prefix="$"
                  />
                </Field>
                <Field label="Sale price ($)">
                  <NumberInput
                    value={inputs.salePrice}
                    onChange={(v) => updateInput('salePrice', v)}
                    placeholder="19.99"
                    prefix="$"
                  />
                </Field>
              </div>
              <Field label="Category">
                <select
                  value={inputs.categoryId}
                  onChange={(e) => updateInput('categoryId', e.target.value)}
                  className={inputClass}
                >
                  {REFERRAL_FEES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
            </SectionCard>

            <SectionCard title="Size & Weight">
              <Field label="Size tier">
                <select
                  value={inputs.sizeTierId}
                  onChange={(e) => updateInput('sizeTierId', e.target.value)}
                  className={inputClass}
                >
                  {FBA_SIZE_TIERS.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Weight (oz)" warning={weightWarning}>
                <NumberInput
                  value={inputs.weightOz}
                  onChange={(v) => updateInput('weightOz', v)}
                  placeholder="12"
                  step="0.1"
                />
              </Field>
              <Field
                label="Dimensions L × W × H (in)"
                warning={dimsWarning}
                hint="Used for FBA storage fee calculation"
              >
                <div className="grid grid-cols-3 gap-2">
                  <NumberInput
                    value={inputs.dimensions.length}
                    onChange={(v) => updateDimension('length', v)}
                    placeholder="L"
                    step="0.1"
                  />
                  <NumberInput
                    value={inputs.dimensions.width}
                    onChange={(v) => updateDimension('width', v)}
                    placeholder="W"
                    step="0.1"
                  />
                  <NumberInput
                    value={inputs.dimensions.height}
                    onChange={(v) => updateDimension('height', v)}
                    placeholder="H"
                    step="0.1"
                  />
                </div>
              </Field>
            </SectionCard>

            <SectionCard title="Shipping & Storage">
              {(mode === 'fba' || mode === 'compare') && (
                <Field label="FBA inbound shipping (per unit)">
                  <NumberInput
                    value={inputs.inboundShipping}
                    onChange={(v) => updateInput('inboundShipping', v)}
                    placeholder="0.75"
                    prefix="$"
                  />
                </Field>
              )}
              {(mode === 'fbm' || mode === 'compare') && (
                <Field label="FBM outbound shipping (per unit)">
                  <NumberInput
                    value={inputs.outboundShipping}
                    onChange={(v) => updateInput('outboundShipping', v)}
                    placeholder="4.50"
                    prefix="$"
                  />
                </Field>
              )}
              {(mode === 'fba' || mode === 'compare') && (
                <Field label="Storage months" hint="Avg months in FBA before selling">
                  <NumberInput
                    value={inputs.storageMonths}
                    onChange={(v) => updateInput('storageMonths', v)}
                    placeholder="1"
                    step="1"
                  />
                </Field>
              )}
            </SectionCard>

            <SectionCard title="Other Costs">
              <Field label="Misc / prep / packaging">
                <NumberInput
                  value={inputs.miscCost}
                  onChange={(v) => updateInput('miscCost', v)}
                  placeholder="0.50"
                  prefix="$"
                />
              </Field>
              <Field label="Advertising (PPC)">
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => updateInput('ppcMode', 'flat')}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition ${
                      inputs.ppcMode === 'flat'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-900/60 text-slate-400 hover:text-white'
                    }`}
                  >
                    Flat $ per unit
                  </button>
                  <button
                    type="button"
                    onClick={() => updateInput('ppcMode', 'acos')}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition ${
                      inputs.ppcMode === 'acos'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-900/60 text-slate-400 hover:text-white'
                    }`}
                  >
                    ACoS %
                  </button>
                </div>
                {inputs.ppcMode === 'flat' ? (
                  <NumberInput
                    value={inputs.ppcFlat}
                    onChange={(v) => updateInput('ppcFlat', v)}
                    placeholder="1.50"
                    prefix="$"
                  />
                ) : (
                  <NumberInput
                    value={inputs.ppcAcos}
                    onChange={(v) => updateInput('ppcAcos', v)}
                    placeholder="0.15 (= 15%)"
                    step="0.01"
                  />
                )}
              </Field>
            </SectionCard>

            <SectionCard title="Target Profit (optional)" icon={Target}>
              <Field
                label="Desired profit per unit"
                hint="Enter a target and we'll tell you what sale price you need to list at"
              >
                <NumberInput
                  value={inputs.targetProfit}
                  onChange={(v) => updateInput('targetProfit', v)}
                  placeholder="5.00"
                  prefix="$"
                />
              </Field>
              {hasTargetInputs && (
                <div className="rounded-lg bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-indigo-300 mb-2 flex items-center gap-1.5">
                    <Target size={13} />
                    Required sale price
                  </div>
                  {mode === 'compare' ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs text-slate-400 mb-0.5">FBA</div>
                        <div className="text-xl font-bold text-white tabular-nums">
                          {requiredPriceFBA !== null && isFinite(requiredPriceFBA)
                            ? `$${requiredPriceFBA.toFixed(2)}`
                            : '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 mb-0.5">FBM</div>
                        <div className="text-xl font-bold text-white tabular-nums">
                          {requiredPriceFBM !== null && isFinite(requiredPriceFBM)
                            ? `$${requiredPriceFBM.toFixed(2)}`
                            : '—'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-2xl font-bold text-white tabular-nums">
                      {(() => {
                        const price = mode === 'fba' ? requiredPriceFBA : requiredPriceFBM;
                        return price !== null && isFinite(price) ? `$${price.toFixed(2)}` : '—';
                      })()}
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-2">
                    Listing at this price yields ${targetProfitNum.toFixed(2)} profit per unit after all fees.
                  </p>
                </div>
              )}
            </SectionCard>

            {hasInputs && (
              <button
                onClick={() => setSaveModalOpen(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold transition-all duration-300 transform hover:scale-[1.02]"
              >
                <Save size={18} />
                Save calculation
              </button>
            )}
          </div>

          {/* RIGHT: Results */}
          <div className="space-y-5">
            {mode === 'compare' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <AmazonFeeBreakdown result={fbaResult} title="FBA" hasInputs={hasInputs} />
                <AmazonFeeBreakdown result={fbmResult} title="FBM" hasInputs={hasInputs} />
              </div>
            ) : mode === 'fba' ? (
              <AmazonFeeBreakdown result={fbaResult} title="FBA Result" hasInputs={hasInputs} />
            ) : (
              <AmazonFeeBreakdown result={fbmResult} title="FBM Result" hasInputs={hasInputs} />
            )}
          </div>
        </div>
      </div>

      {/* Saved drawer */}
      {savedDrawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setSavedDrawerOpen(false)}
        >
          <div
            className="absolute right-0 top-0 h-full w-full sm:w-96 bg-slate-900 border-l border-slate-700 shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Bookmark size={20} className="text-indigo-400" />
                Saved calculations
              </h2>
              <button
                onClick={() => setSavedDrawerOpen(false)}
                className="p-1 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white"
                aria-label="Close saved drawer"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              {loadingSaved ? (
                <div className="flex items-center justify-center py-10 text-slate-400">
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Loading...
                </div>
              ) : savedError ? (
                <div className="text-center py-10">
                  <p className="text-red-400 mb-3 text-sm">{savedError}</p>
                  <button
                    onClick={loadSaved}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium"
                  >
                    Retry
                  </button>
                </div>
              ) : savedCalcs.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-10">
                  No saved calculations yet.
                  <br />
                  Fill in the form and click Save.
                </p>
              ) : (
                <div className="space-y-2">
                  {savedCalcs.map((calc) => (
                    <div
                      key={calc.id}
                      className="group bg-slate-800/70 hover:bg-slate-800 border border-slate-700 rounded-lg p-3 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button
                          onClick={() => handleLoadCalc(calc)}
                          className="flex-1 text-left"
                        >
                          <div className="font-semibold text-white text-sm">{calc.label}</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {calc.mode?.toUpperCase()} · ${Number(calc.inputs?.salePrice || 0).toFixed(2)} sale
                          </div>
                        </button>
                        <button
                          onClick={() => handleDeleteCalc(calc.id)}
                          className="p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          aria-label={`Delete ${calc.label}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save modal */}
      {saveModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSaveModalOpen(false)}
        >
          <div
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Save size={20} className="text-indigo-400" />
              Save calculation
            </h2>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
              Label
            </label>
            <input
              type="text"
              value={saveLabel}
              onChange={(e) => setSaveLabel(e.target.value)}
              placeholder={`Calculation ${new Date().toLocaleDateString()}`}
              className={inputClass}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
              }}
            />
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setSaveModalOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-medium"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl shadow-2xl backdrop-blur-md border text-sm font-medium flex items-center gap-2 animate-fade-in ${
            toast.type === 'success'
              ? 'bg-emerald-600/90 border-emerald-500 text-white'
              : 'bg-red-600/90 border-red-500 text-white'
          }`}
        >
          {toast.type === 'success' ? <Check size={16} /> : <AlertTriangle size={16} />}
          {toast.message}
        </div>
      )}
    </div>
  );
}
